import React, { createRef, Component, RefObject } from "react";
import Button from "../Button";
import ReactGA from "react-ga";
import SearchPageModel from "./components/SearchPageModel";
import { observer } from "mobx-react";
import ImgLazyLoad from "../ImgLazyLoad";
import { NavMenuPath } from "../../utils/ContentMediaPath";

interface Props {
  baseUrl?: string;
  cdnBasePath: string;
}

interface State {
  showSearchBar: boolean;
  isSearching: boolean;
  searchQuery: string;
  recentSearches: string[];
  inputFocused: boolean;
}

@observer
export class ClientHeader extends Component<Props, State> {
  private searchPageModel: SearchPageModel;

  constructor(props: Props) {
    super(props);
    this.state = {
      showSearchBar: false,
      searchQuery: "",
      isSearching: false,
      recentSearches: this.getRecentSearches(),
      inputFocused: false,
    };

    this.searchPageModel = new SearchPageModel();
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.handleSuggestionClick = this.handleSuggestionClick.bind(this);
    this.handleDeleteRecentSearch = this.handleDeleteRecentSearch.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.isSearchVisible = this.isSearchVisible.bind(this);
    this.hasRecentSearches = this.hasRecentSearches.bind(this);
    this.hasSearchResults = this.hasSearchResults.bind(this);
  }

  componentDidMount() {
    document.addEventListener("click", this.handleClickOutside.bind(this));
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleClickOutside.bind(this));
  }

  handleInputBlur = () => {
    this.setState({ inputFocused: false });
  };

  hasRecentSearches = () => {
    const { searchQuery, recentSearches } = this.state;
    return !searchQuery && recentSearches.length > 0;
  };

  hasSearchResults = () => {
    const { searchQuery } = this.state;
    return searchQuery && this.searchPageModel.suggestions.length > 0;
  };

  isSearchVisible = () => {
    const { inputFocused } = this.state;
    const hasSearchResults = this.hasSearchResults();
    const hasRecentSearches = this.hasRecentSearches();

    return inputFocused && (hasSearchResults || hasRecentSearches);
  };

  handleClickOutside = (event: React.MouseEvent) => {
    const targetElement = [
      "search-input",
      "search-list-container",
      "suggestions-ul-list",
      "suggestions-list",
      "deleteButton",
    ];

    const classNames = (event.target as HTMLElement).classList;
    const hasTargetClass = targetElement.some((element) =>
      classNames.contains(element)
    );
    if (!hasTargetClass) {
      this.searchPageModel.reset();
    }
    this.setState({ inputFocused: hasTargetClass });
  };

  handleSearch = async () => {
    if (this.state.searchQuery.trim() !== "") {
      const { cdnBasePath } = this.props;
      const { searchQuery } = this.state;
      this.saveRecentSearch(searchQuery);
      this.setState({ isSearching: true });
      const searchUrl = `${cdnBasePath}/search?query=${encodeURIComponent(
        searchQuery
      )}`;

      try {
        window.location.href = searchUrl;
      } catch (error) {
        console.error("Error Searching", error);
      } finally {
        this.setState({ isSearching: false });
      }
    }
  };

  handleClear = () => {
    this.setState({ searchQuery: "" });
  };

  handleSuggestionClick = (suggestion: string) => {
    const { cdnBasePath } = this.props;
    this.saveRecentSearch(suggestion);
    this.searchPageModel.reset();
    const searchUrl = `${cdnBasePath}/search?query=${encodeURIComponent(
      suggestion
    )}`;
    window.location.href = searchUrl;
  };

  handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { cdnBasePath } = this.props;
    const inputValue = e.target.value;
    this.setState({ searchQuery: inputValue }, () => {
      this.searchPageModel.fetchSearchSuggestions(inputValue, cdnBasePath);
    });
  };

  handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      this.handleSearch();
    }
  };

  getRecentSearches() {
    const recentSearches = localStorage.getItem("recentSearches");
    return recentSearches ? JSON.parse(recentSearches) : [];
  }

  handleInputFocus = () => {
    console.log("getRecentSearches()", this.getRecentSearches());
    this.setState({ inputFocused: true });
  };

  saveRecentSearch(query: string) {
    const truncatedQuery = query.length > 50 ? query.substring(0, 50) : query;
    const { recentSearches } = this.state;
    if (recentSearches.indexOf(truncatedQuery) === -1) {
      const updatedSearches = [truncatedQuery, ...recentSearches].slice(0, 5);
      this.setState({ recentSearches: updatedSearches });
      localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
    }
  }

  handleDeleteRecentSearch = (search: string) => {
    const updatedSearches = this.state.recentSearches.filter(
      (item) => item !== search
    );
    this.setState({ recentSearches: updatedSearches });
    localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
  };

  render() {
    const { baseUrl } = this.props;
    const {
      showSearchBar,
      searchQuery,
      isSearching,
      recentSearches,
      inputFocused,
    } = this.state;

    const currentUrl = window.location.href;
    const isSearchPage = currentUrl.includes("/search");

    return (
      <div id="website-header">
        <div id="mobile-menu" className="mobile-menu">
          <br />
          <ul>
            <li className="dropdown-element">
              <a className="icon-car" href={baseUrl + "/showroom"}>
                Vehicles
              </a>
              <div className="dropdown-arrow"></div>
              <div className="dropdown-block">
                <ul className="sub-menu">
                  <li>
                    <a href={baseUrl + "/crosstrek"}>All-new Crosstrek</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/hybrid"}>All-new Crosstrek Hybrid</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/solterra"}>All-new Solterra</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/forester"}>Forester</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/hybrid"}>Forester Hybrid</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/outback"}>Outback</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/impreza"}>All-new Impreza</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/brz"}>BRZ</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/wrx"}>WRX</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/wrx"}>WRX Sportswagon</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/wrx-sti"}>WRX STI</a>
                  </li>
                </ul>
              </div>
            </li>
            <li>
              <a
                className="icon-star-pin"
                href={baseUrl + "/great-australian-detour"}
              >
                Great Australian Detour
              </a>
            </li>
            <li>
              <a className="icon-heart" href={baseUrl + "/why-subaru"}>
                Why Subaru
              </a>
            </li>
            <li>
              <a
                className="icon-price-tag"
                href={baseUrl + "/special-offers"}
                onClick={() =>
                  ReactGA.ga(
                    "send",
                    "event",
                    "CTA Floating Footer",
                    "CTA Floating Footer Click b",
                    "/special-offers",
                    1234
                  )
                }
              >
                Offers
              </a>
            </li>
            <li className="main-menu-owners dropdown-element">
              <a className="icon-owner" href={baseUrl + "/owners"}>
                Owners
              </a>
              <div className="dropdown-arrow"></div>
              <div className="dropdown-block">
                <ul className="sub-menu">
                  <li>
                    <a href={baseUrl + "/owners/servicing"}>Servicing</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/owners/parts-accessories"}>
                      Parts &amp; Accessories
                    </a>
                  </li>
                  <li>
                    <a href={baseUrl + "/owners/warranty"}>Warranty</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/owners/collision-roadside"}>
                      Collision &amp; Roadside
                    </a>
                  </li>
                  <li>
                    <a href={baseUrl + "/owners/recalls"}>Recalls</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/owners/owners-resources"}>
                      Owners Resources
                    </a>
                  </li>
                </ul>
              </div>
            </li>
            <li className="main-menu-buyers dropdown-element">
              <a className="icon-shopping-bag" href="#">
                Buyers Tools
              </a>
              <div className="dropdown-arrow"></div>
              <div className="dropdown-block">
                <ul className="sub-menu">
                  <li>
                    <a href={baseUrl + "/configure"}>
                      Build &amp; Price Your Own Subaru
                    </a>
                  </li>
                  <li>
                    <a href={baseUrl + "/buy-online"}>Buy Your Subaru Online</a>
                  </li>
                  <li>
                    <a
                      href={baseUrl + "/brochure-download"}
                      onClick={() =>
                        ReactGA.ga(
                          "send",
                          "event",
                          "CTA Floating Footer",
                          "CTA Floating Footer Click b",
                          "/brochure-download",
                          2719
                        )
                      }
                    >
                      Request A Brochure{" "}
                    </a>
                  </li>
                  <li>
                    <a
                      href={baseUrl + "/book-test-drive"}
                      onClick={() =>
                        ReactGA.ga(
                          "send",
                          "event",
                          "CTA Floating Footer",
                          "CTA Floating Footer Click b",
                          "/book-test-drive",
                          581
                        )
                      }
                    >
                      Book A Test Drive
                    </a>
                  </li>
                  <li>
                    <a
                      href={baseUrl + "/dealers"}
                      onClick={() =>
                        ReactGA.ga(
                          "send",
                          "event",
                          "CTA Floating Footer",
                          "CTA Floating Footer Click b",
                          "retailers",
                          3689
                        )
                      }
                    >
                      Find A Retailer
                    </a>
                  </li>
                  <li>
                    <a
                      href={baseUrl + "/value-my-car"}
                      onClick={() =>
                        ReactGA.ga(
                          "send",
                          "event",
                          "CTA Floating Footer",
                          "CTA Floating Footer Click b",
                          "/value-my-car",
                          1234
                        )
                      }
                    >
                      Value My Car
                    </a>
                  </li>
                  <li>
                    <a href={baseUrl + "/financial-services"}>
                      Financial Services
                    </a>
                  </li>
                  <li>
                    <a href={baseUrl + "/premier-motor-insurance"}>Insurance</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/fleet"}>Fleet &amp; Corporate Sales</a>
                  </li>
                  <li>
                    <a href={baseUrl + "/drivezy-car-subscription"}>
                      Subaru drivezy<sup>TM</sup> Subscription
                    </a>
                  </li>
                  <li>
                    <a href={baseUrl + "/contact-us"}>Contact Us</a>
                  </li>
                </ul>
              </div>
            </li>
            <li>
              <a className="icon-car" href={baseUrl + "/used"}>
                Used Cars
              </a>
            </li>
            <li className="main-menu-batd inverse-element">
              <a
                className="icon-steering-wheel-2"
                href={baseUrl + "/book-test-drive"}
              >
                Book a Test Drive
              </a>
            </li>
          </ul>
        </div>
        <div id="header-nav">
          <nav id="mobile-nav" className="mobile-nav">
            <div className="header-container">
              <div className="row">
                <div className="mobile-nav-wrapper">
                  <div className="mobile-menu-button">
                    <a className="menu-open" href="#"></a>
                  </div>
                  <div className="header-logo">
                    <a href={baseUrl + "/"}>
                      <ImgLazyLoad
                        src={NavMenuPath("logo-mobile.png")}
                        alt="Subaru"
                        title="Subaru"
                      />
                    </a>
                  </div>
                  <div className="mobile-menu-button mobile-menu-button-right">
                    <div className="dropdown-container">
                      <div className="dropdown">
                        <div>
                          <div className="dropbtn"></div>
                          <div className="dropdown-content">
                            <a href="/my-account">My Account</a>
                            <a href="/build-your-own/profile">
                              My Online Orders
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                    {isSearchPage ? (
                      <button
                        onClick={() => window.location.reload()}
                        className="menu-search"
                      />
                    ) : (
                      <button
                        onClick={() =>
                          this.setState({ showSearchBar: !showSearchBar })
                        }
                        className="menu-search"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            {!isSearchPage && showSearchBar && (
              <div
                className={`search-page-container ${
                  showSearchBar ? "active" : ""
                }`}
              >
                <div className="search-bg"></div>
                <div className="search-popup">
                  <input
                    type="text"
                    value={searchQuery}
                    className="search-input"
                    onChange={this.handleTextChange}
                    onKeyDown={this.handleKeyDown}
                    placeholder="Search for something"
                  />
                  {searchQuery && (
                    <div className="iconclose" onClick={this.handleClear} />
                  )}
                  <Button
                    loading={isSearching}
                    className={"iconsearch"}
                    onClick={this.handleSearch}
                  />
                </div>
                {this.isSearchVisible() && (
                  <div className="search-list-container">
                    <ul className="suggestions-ul-list">
                      {this.hasRecentSearches()
                        ? recentSearches.map((recent, index) => (
                            <li
                              key={index}
                              className="suggestions-list"
                              onClick={() => this.handleSuggestionClick(recent)}
                              style={{ color: "#406DBA" }}
                            >
                              {recent}
                              <span
                                className="deleteButton"
                                onClick={(e) => {
                                  this.handleDeleteRecentSearch(recent);
                                }}
                              >
                                &times;
                              </span>
                            </li>
                          ))
                        : this.searchPageModel.suggestions.map(
                            (suggestion, index) => (
                              <li
                                key={index}
                                className="suggestions-list"
                                onClick={() =>
                                  this.handleSuggestionClick(suggestion)
                                }
                              >
                                {suggestion}
                              </li>
                            )
                          )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </nav>
          <nav className="primary-nav">
            <div className="header-container">
              <div className="row">
                <div className="header-logo">
                  <a href={baseUrl + "/"}>
                    <ImgLazyLoad
                      src={NavMenuPath("logo.png")}
                      alt="Subaru"
                      title="Subaru"
                    />
                  </a>
                </div>
                <div className="main-menu">
                  <ul>
                    <li className="dropdown-element">
                      <a href={baseUrl + "/showroom"}>Vehicles</a>
                      <div className="dropdown-block dropdown-mega-block">
                        <div className="header-container mega-menu custom">
                          <div className="section-vehicles">
                            <div className="row">
                              <div className="col-sm-2 col-sm-2-custom">
                                <div className="menu-item">
                                  <div className="thumbnail">
                                    <a href={baseUrl + "/crosstrek"}>
                                      <ImgLazyLoad
                                        src={
                                          NavMenuPath("menu-crosstrek-awd.png")
                                        }
                                        alt="Crosstrek"
                                      />
                                    </a>
                                  </div>
                                  <div className="caption">
                                    <a
                                      href={baseUrl + "/crosstrek"}
                                      className="display-inline"
                                    >
                                      ALL-NEW CROSSTREK
                                    </a>
                                    <a
                                      href={baseUrl + "/hybrid"}
                                      className="hybrid-link"
                                    >
                                      &nbsp;(Hybrid)
                                    </a>
                                  </div>
                                  <div className="menu-links">
                                    <div className="menu-link menu-link-overview">
                                      <a href={baseUrl + "/crosstrek"}>
                                        Overview
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-byo">
                                      <a href={baseUrl + "/configure"}>
                                        BUILD YOUR OWN
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-batd">
                                      <a
                                        href={
                                          baseUrl +
                                          "/book-test-drive?model=crosstrek"
                                        }
                                      >
                                        Book a test drive
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-2 col-sm-2-custom">
                                <div className="menu-item">
                                  <div className="thumbnail">
                                    <a href={baseUrl + "/solterra"}>
                                      <ImgLazyLoad
                                        src={
                                          NavMenuPath("my24-solterra.png")
                                        }
                                        alt="solterra"
                                      />
                                    </a>
                                  </div>
                                  <div className="caption">
                                    <a
                                      href={baseUrl + "/solterra"}
                                      className="display-inline"
                                    >
                                      ALL-NEW SOLTERRA
                                    </a>
                                    <a
                                      href={baseUrl + "/solterra"}
                                      className="hybrid-link"
                                    >
                                      Electric
                                    </a>
                                  </div>
                                  <div className="menu-links">
                                    <div className="menu-link menu-link-overview">
                                      <a href={baseUrl + "/solterra"}>
                                        Overview
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-byo">
                                      <a href={baseUrl + "/configure"}>
                                        Build your own
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-batd">
                                      <a
                                        href={
                                          baseUrl +
                                          "/book-test-drive?model=solterra"
                                        }
                                      >
                                        Book a test drive
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-2 col-sm-2-custom">
                                <div className="menu-item">
                                  <div className="thumbnail">
                                    <a href={baseUrl + "/forester"}>
                                      <ImgLazyLoad
                                        src={
                                          NavMenuPath("my24-forester_bluemetallic.png")
                                        }
                                        alt="forester"
                                      />
                                    </a>
                                  </div>
                                  <div className="caption">
                                    <a
                                      href={baseUrl + "/forester"}
                                      className="display-inline"
                                    >
                                      Forester
                                    </a>
                                    <a
                                      href={baseUrl + "/hybrid"}
                                      className="hybrid-link"
                                    >
                                      &nbsp;(Hybrid)
                                    </a>
                                  </div>
                                  <div className="menu-links">
                                    <div className="menu-link menu-link-overview">
                                      <a href={baseUrl + "/forester"}>
                                        Overview
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-byo">
                                      <a href={baseUrl + "/configure"}>
                                        Build your own
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-batd">
                                      <a
                                        href={
                                          baseUrl +
                                          "/book-test-drive?model=forester"
                                        }
                                      >
                                        Book a test drive
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-2 col-sm-2-custom">
                                <div className="menu-item">
                                  <div className="thumbnail">
                                    <a href={baseUrl + "/outback"}>
                                      <ImgLazyLoad
                                        src={
                                          NavMenuPath("menu-outback.png")
                                        }
                                        alt="Outback"
                                      />
                                    </a>
                                  </div>
                                  <div className="caption">
                                    <a
                                      href={baseUrl + "/outback"}
                                      className="display-inline"
                                    >
                                      Outback
                                    </a>
                                  </div>
                                  <div className="menu-links">
                                    <div className="menu-link menu-link-overview">
                                      <a href={baseUrl + "/outback"}>
                                        Overview
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-byo">
                                      <a href={baseUrl + "/configure"}>
                                        BUILD YOUR OWN
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-batd">
                                      <a
                                        href={
                                          baseUrl +
                                          "/book-test-drive?model=outback"
                                        }
                                      >
                                        Book a test drive
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-2 col-sm-2-custom">
                                <div className="icon-megamenu-close">
                                  <a href="#">
                                    <i className="icon icon-circle-close icon-2x"></i>
                                  </a>
                                </div>
                                <div className="menu-item">
                                  <div className="thumbnail">
                                    <a href={baseUrl + "/impreza"}>
                                      <ImgLazyLoad
                                        src={
                                          NavMenuPath("my24-impreza-2.png")
                                        }
                                        alt="Impreza"
                                      />
                                    </a>
                                  </div>
                                  <div className="caption">
                                    <a href={baseUrl + "/impreza"}>
                                      All-NEW IMPREZA
                                    </a>
                                  </div>
                                  <div className="menu-links">
                                    <div className="menu-link menu-link-overview">
                                      <a href={baseUrl + "/impreza"}>
                                        Overview
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-byo">
                                      <a href={baseUrl + "/configure"}>
                                        BUILD YOUR OWN
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-batd">
                                      <a
                                        href={
                                          baseUrl +
                                          "/book-test-drive?model=impreza"
                                        }
                                      >
                                        Book a test drive
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-sm-2 col-sm-2-custom">
                                <div className="menu-item">
                                  <div className="thumbnail">
                                    <a href={baseUrl + "/brz"}>
                                      <ImgLazyLoad
                                        src={
                                          NavMenuPath("my24-menu-brz.png")
                                        }
                                        alt="brz"
                                      />
                                    </a>
                                  </div>
                                  <div className="caption">
                                    <a href={baseUrl + "/brz"}>BRZ</a>
                                  </div>
                                  <div className="menu-links">
                                    <div className="menu-link menu-link-overview">
                                      <a href={baseUrl + "/brz"}>OVERVIEW</a>
                                    </div>
                                    <div className="menu-link menu-link-byo">
                                      <a href={baseUrl + "/configure"}>
                                        Build your own
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-batd">
                                      <a
                                        href={
                                          baseUrl + "/book-test-drive?model=brz"
                                        }
                                      >
                                        Book a test drive
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-2 col-sm-2-custom">
                                <div className="menu-item">
                                  <div className="thumbnail">
                                    <a href={baseUrl + "/wrx"}>
                                      <ImgLazyLoad
                                        src={NavMenuPath("my24-wrx.png")}
                                        alt="wrx"
                                      />
                                    </a>
                                  </div>
                                  <div className="caption">
                                    <a href={baseUrl + "/wrx"}>WRX</a>
                                  </div>
                                  <div className="menu-links">
                                    <div className="menu-link menu-link-overview">
                                      <a href={baseUrl + "/wrx"}>OVERVIEW</a>
                                    </div>
                                    <div className="menu-link menu-link-byo">
                                      <a href={baseUrl + "/configure"}>
                                        BUILD YOUR OWN
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-batd">
                                      <a
                                        href={
                                          baseUrl + "/book-test-drive?model=wrx"
                                        }
                                      >
                                        Book a test drive
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-2 col-sm-2-custom">
                                <div className="menu-item">
                                  <div className="thumbnail">
                                    <a href={baseUrl + "/wrx"}>
                                      <ImgLazyLoad
                                        src={
                                          NavMenuPath("my24-wrx-sportswagon.png")
                                        }
                                        alt="wrx"
                                      />
                                    </a>
                                  </div>
                                  <div className="caption">
                                    <a href={baseUrl + "/wrx"}>
                                      WRX SPORTSWAGON
                                    </a>
                                  </div>
                                  <div className="menu-links">
                                    <div className="menu-link menu-link-overview">
                                      <a href={baseUrl + "/wrx"}>OVERVIEW</a>
                                    </div>
                                    <div className="menu-link menu-link-byo">
                                      <a href={baseUrl + "/configure"}>
                                        BUILD YOUR OWN
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-batd">
                                      <a
                                        href={
                                          baseUrl + "/book-test-drive?model=wrx"
                                        }
                                      >
                                        Book a test drive
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-2 col-sm-2-custom">
                                <div className="menu-item">
                                  <div className="thumbnail">
                                    <a href={baseUrl + "/wrx-sti"}>
                                      <ImgLazyLoad
                                        src={
                                          NavMenuPath("menu-wrx-sti.png")
                                        }
                                        alt="wrx-sti"
                                      />
                                    </a>
                                  </div>
                                  <div className="caption">
                                    <a href={baseUrl + "/wrx-sti"}>WRX STI</a>
                                  </div>
                                  <div className="menu-links">
                                    <div className="menu-link menu-link-overview">
                                      <a href={baseUrl + "/wrx-sti"}>HISTORY</a>
                                    </div>
                                    <div className="menu-link menu-link-byo">
                                      <a href={baseUrl + "/subscribe"}>
                                        SUBSCRIBE TO SUBARU
                                      </a>
                                    </div>
                                    <div className="menu-link menu-link-batd">
                                      <a
                                        href={
                                          baseUrl + "/book-test-drive?model=wrx"
                                        }
                                      >
                                        Book a test drive
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                    <li>
                      <a href={baseUrl + "/great-australian-detour"}>
                        Great Australian Detour
                      </a>
                    </li>
                    <li>
                      <a href={baseUrl + "/why-subaru"}>Why Subaru</a>
                    </li>
                    <li>
                      <a
                        href={baseUrl + "/special-offers"}
                        onClick={() =>
                          ReactGA.ga(
                            "send",
                            "event",
                            "CTA Floating Footer",
                            "CTA Floating Footer Click b",
                            "/special-offers",
                            1234
                          )
                        }
                      >
                        Offers
                      </a>
                    </li>
                    <li className="dropdown-element">
                      <a href={baseUrl + "/owners"}>Owners</a>
                      <div className="dropdown-block">
                        <ul className="sub-menu">
                          <li>
                            <a href={baseUrl + "/owners/servicing"}>
                              Servicing
                            </a>
                          </li>
                          <li>
                            <a href={baseUrl + "/owners/parts-accessories"}>
                              Parts &amp; Accessories
                            </a>
                          </li>
                          <li>
                            <a href={baseUrl + "/owners/warranty"}>Warranty</a>
                          </li>
                          <li>
                            <a href={baseUrl + "/owners/collision-roadside"}>
                              Collision &amp; Roadside
                            </a>
                          </li>
                          <li>
                            <a href={baseUrl + "/owners/recalls"}>Recalls</a>
                          </li>
                          <li>
                            <a href={baseUrl + "/owners/owners-resources"}>
                              Owners Resources
                            </a>
                          </li>
                        </ul>
                      </div>
                    </li>
                    <li className="dropdown-element">
                      <a href={baseUrl + "/configure"}>Buyers Tools</a>
                      <div className="dropdown-block">
                        <ul className="sub-menu">
                          <li>
                            <a href={baseUrl + "/configure"}>
                              Build &amp; Price Your Own Subaru
                            </a>
                          </li>
                          <li>
                            <a href={baseUrl + "/buy-online"}>
                              Buy Your Subaru Online
                            </a>
                          </li>
                          <li>
                            <a
                              href={baseUrl + "/brochure-download"}
                              onClick={() =>
                                ReactGA.ga(
                                  "send",
                                  "event",
                                  "CTA Floating Footer",
                                  "CTA Floating Footer Click b",
                                  "/brochure-download",
                                  2719
                                )
                              }
                            >
                              Request A Brochure{" "}
                            </a>
                          </li>
                          <li>
                            <a
                              href={baseUrl + "/book-test-drive"}
                              onClick={() =>
                                ReactGA.ga(
                                  "send",
                                  "event",
                                  "CTA Floating Footer",
                                  "CTA Floating Footer Click b",
                                  "/book-test-drive",
                                  581
                                )
                              }
                            >
                              Book A Test Drive
                            </a>
                          </li>
                          <li>
                            <a
                              href={baseUrl + "/dealers"}
                              onClick={() =>
                                ReactGA.ga(
                                  "send",
                                  "event",
                                  "CTA Floating Footer",
                                  "CTA Floating Footer Click b",
                                  "retailers",
                                  3689
                                )
                              }
                            >
                              Find A Retailer
                            </a>
                          </li>
                          <li>
                            <a
                              href={baseUrl + "/value-my-car"}
                              onClick={() =>
                                ReactGA.ga(
                                  "send",
                                  "event",
                                  "CTA Floating Footer",
                                  "CTA Floating Footer Click b",
                                  "/value-my-car",
                                  1234
                                )
                              }
                            >
                              Value My Car
                            </a>
                          </li>
                          <li>
                            <a href={baseUrl + "/financial-services"}>
                              Financial Services
                            </a>
                          </li>
                          <li>
                            <a href={baseUrl + "/premier-motor-insurance"}>
                              Insurance
                            </a>
                          </li>
                          <li>
                            <a href={baseUrl + "/fleet"}>
                              Fleet &amp; Corporate Sales
                            </a>
                          </li>
                          <li>
                            <a href={baseUrl + "/drivezy-car-subscription"}>
                              Subaru drivezy<sup>TM</sup> Subscription
                            </a>
                          </li>
                          <li>
                            <a href={baseUrl + "/contact-us"}>Contact Us</a>
                          </li>
                        </ul>
                      </div>
                    </li>
                    <li>
                      <a href={baseUrl + "/used"}>Used Cars</a>
                    </li>
                    <li className="dropdown-container">
                      <div className="dropdown">
                        <div>
                          <div className="dropbtn"></div>
                          <div className="dropdown-content">
                            <a href="/my-account">My Account</a>
                            <a href="/build-your-own/profile">
                              My Online Orders
                            </a>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                  <div id="site-search" className="site-search">
                    {isSearchPage ? (
                      <button
                        onClick={() => window.location.reload()}
                        className="icon icon-search site-search-show"
                      />
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          this.setState({ showSearchBar: !showSearchBar });
                        }}
                        className="icon icon-search site-search-show"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            {!isSearchPage && showSearchBar && (
              <div
                className={`search-page-container ${
                  showSearchBar ? "active" : ""
                }`}
              >
                <div className="search-bg"></div>
                <div className="search-popup">
                  <input
                    type="text"
                    value={searchQuery}
                    className="search-input"
                    onChange={this.handleTextChange}
                    onKeyDown={this.handleKeyDown}
                    placeholder="Search for something"
                  />
                  {searchQuery && (
                    <div className="iconclose" onClick={this.handleClear} />
                  )}
                  <Button
                    loading={isSearching}
                    className={"iconsearch"}
                    onClick={this.handleSearch}
                  />
                </div>
                {this.isSearchVisible() && (
                  <div className="search-list-container">
                    <ul className="suggestions-ul-list">
                      {this.hasRecentSearches()
                        ? recentSearches.map((recent, index) => (
                            <li
                              key={index}
                              className="suggestions-list"
                              onClick={() => this.handleSuggestionClick(recent)}
                              style={{ color: "#406DBA" }}
                            >
                              {recent}
                              <span
                                className="deleteButton"
                                onClick={(e) => {
                                  this.handleDeleteRecentSearch(recent);
                                }}
                              >
                                &times;
                              </span>
                            </li>
                          ))
                        : this.searchPageModel.suggestions.map(
                            (suggestion, index) => (
                              <li
                                key={index}
                                className="suggestions-list"
                                onClick={() =>
                                  this.handleSuggestionClick(suggestion)
                                }
                              >
                                {suggestion}
                              </li>
                            )
                          )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      </div>
    );
  }
}
